import { useEffect, useState } from "react";
import { Glyph } from "@apf/design-system/glyphs";
import { assetHref } from "./routerMount";

/**
 * Shared access to the generated site-safe asset manifest. The manifest and
 * every file it lists are produced by tooling/generate-site-assets.ts from the
 * audience-isolated site projection; consumers render its rows and never
 * fabricate an asset.
 */

export type AssetFormat = { format: string; path: string; bytes: number; sha256: string };

export type ManifestAsset = {
  assetId: string;
  assetClass: string;
  title: string;
  version: string;
  proof: { classId: string; label: string };
  licence: { id: string; label: string };
  compatibility: { frameworkVersion: string };
  source: { objectType: string; objectId: string };
  canonicalRoute: string;
  formats: AssetFormat[];
};

export type AssetManifest = {
  generatorVersion: string;
  assetCount: number;
  compatibility: { frameworkVersion: string; partnerVersion: string };
  assets: ManifestAsset[];
};

export const copyableFormats = new Set(["tsx", "markdown", "json", "json-schema", "yaml"]);

export function useAssetManifest(): { manifest: AssetManifest | null; failed: boolean } {
  const [manifest, setManifest] = useState<AssetManifest | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(assetHref("/downloads/manifest.json"))
      .then((response) => {
        if (!response.ok) throw new Error(`manifest request failed: ${response.status}`);
        return response.json() as Promise<AssetManifest>;
      })
      .then((loaded) => {
        if (!cancelled) setManifest(loaded);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { manifest, failed };
}

type CopyState = "idle" | "copied" | "error";

export function CopyAssetButton({ path, copy }: { path: string; copy: (key: string) => string }) {
  const [state, setState] = useState<CopyState>("idle");

  const handleCopy = async () => {
    try {
      const response = await fetch(assetHref(path));
      if (!response.ok) throw new Error(`asset request failed: ${response.status}`);
      const text = await response.text();
      await navigator.clipboard.writeText(text);
      setState("copied");
    } catch {
      setState("error");
    }
  };

  const label =
    state === "copied"
      ? copy("page.downloads.copied")
      : state === "error"
        ? copy("page.downloads.copy-failed")
        : copy("page.downloads.copy");
  return (
    <button
      className="apf-button apf-button--quiet apf-downloads-copy"
      data-copy-state={state}
      type="button"
      onClick={handleCopy}
    >
      {state === "copied" ? <Glyph meaning="utility.check" size={16} /> : null}
      <span aria-live="polite">{label}</span>
    </button>
  );
}
